
import { supabase } from '@/integrations/supabase/client';

export interface PhotoUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

// Cache for signed URLs to prevent regeneration on every page navigation
interface CachedUrl {
  signedUrl: string;
  expiresAt: number;
}

const urlCache = new Map<string, CachedUrl>();
const CACHE_BUFFER_MS = 10 * 60 * 1000; // Regenerate 10 min before expiry
const URL_EXPIRY_MS = 60 * 60 * 1000; // 1 hour

class PhotoUploadService {
  
  async uploadProfilePhoto(file: File, userId: string): Promise<PhotoUploadResult> {
    try {
      // Generate unique filename with user-specific folder structure
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${timestamp}.${fileExt}`;

      console.log('Uploading profile photo to private storage:', fileName);

      // Upload file to private storage bucket
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return { success: false, error: uploadError.message };
      }

      // For private buckets, we need to create a signed URL for access
      const { data: { signedUrl }, error: signedUrlError } = await supabase.storage
        .from('profile-photos')
        .createSignedUrl(fileName, 60 * 60 * 24 * 365); // 1 year expiry

      if (signedUrlError) {
        console.error('Signed URL error:', signedUrlError);
        return { success: false, error: signedUrlError.message };
      }

      console.log('Photo uploaded successfully with signed URL');
      
      // Track activity (fire-and-forget)
      import('./activityTrackingService').then(({ activityTrackingService }) => {
        activityTrackingService.recordPhotoUpload(userId, 'profile').catch(err =>
          console.error('Failed to track photo upload activity:', err)
        );
      });
      
      // Store the file path (not the signed URL) in the database
      // We'll generate signed URLs when needed
      return { success: true, url: fileName };
    } catch (error) {
      console.error('Error uploading photo:', error);
      return { success: false, error: 'Failed to upload photo' };
    }
  }

  async getProfilePhotoUrl(filePath: string): Promise<string | null> {
    try {
      if (!filePath) return null;

      // Check cache first
      const cached = urlCache.get(filePath);
      const now = Date.now();
      
      if (cached && cached.expiresAt > now + CACHE_BUFFER_MS) {
        // Return cached URL if still valid with buffer time
        return cached.signedUrl;
      }

      // Create a fresh signed URL for viewing the photo
      const { data: { signedUrl }, error } = await supabase.storage
        .from('profile-photos')
        .createSignedUrl(filePath, 60 * 60); // 1 hour expiry for viewing

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      // Cache the new URL
      urlCache.set(filePath, {
        signedUrl,
        expiresAt: now + URL_EXPIRY_MS
      });

      return signedUrl;
    } catch (error) {
      console.error('Error getting photo URL:', error);
      return null;
    }
  }

  // Clear cache for a specific file (useful when replacing photos)
  clearCachedUrl(filePath: string): void {
    urlCache.delete(filePath);
  }

  async deleteProfilePhoto(filePath: string): Promise<boolean> {
    try {
      if (!filePath) return false;

      const { error } = await supabase.storage
        .from('profile-photos')
        .remove([filePath]);

      if (error) {
        console.error('Delete error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting photo:', error);
      return false;
    }
  }

  async replaceProfilePhoto(oldFilePath: string, newFile: File, userId: string): Promise<PhotoUploadResult> {
    try {
      // Clear cached URL for old file
      if (oldFilePath) {
        this.clearCachedUrl(oldFilePath);
        await this.deleteProfilePhoto(oldFilePath);
      }

      // Upload new photo
      return await this.uploadProfilePhoto(newFile, userId);
    } catch (error) {
      console.error('Error replacing photo:', error);
      return { success: false, error: 'Failed to replace photo' };
    }
  }
}

export const photoUploadService = new PhotoUploadService();
